import { FunctionComponent, RefObject } from "react";
import { IconButton } from "./Button";
import { CloseIcon, SearchIcon } from "./icons";

export type FilterFieldProps = {
  value: string;
  onChange: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
};

/**
 * The search box at the top of the list.
 *
 * `type="search"` and not `type="text"`, so a phone keyboard offers a Search key and
 * the platform clear affordance exists for anyone who expects it — but the visible
 * clear button is ours, because WebKit's is invisible until the field has focus and
 * this field is cleared far more often than it is focused.
 */
export const FilterField: FunctionComponent<FilterFieldProps> = ({
  value,
  onChange,
  inputRef,
  autoFocus,
}) => {
  return (
    <div className="relative flex items-center">
      <SearchIcon className="pointer-events-none absolute left-2.5 size-4 text-ink-faint" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        aria-label="Filter names"
        placeholder="Filter"
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && value) {
            // Stops here rather than reaching a dialog or the browser's own find bar:
            // while there is something to clear, Escape means clear it.
            e.stopPropagation();
            onChange("");
          }
        }}
        className="h-9 w-full rounded-md border border-line-strong bg-surface-raised
          pr-9 pl-8 text-ink placeholder:text-ink-faint
          focus:border-accent focus:outline-1 focus:outline-accent
          [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <IconButton
          label="Clear filter"
          onClick={() => onChange("")}
          className="absolute right-0.5 size-8"
        >
          <CloseIcon className="size-4" />
        </IconButton>
      ) : null}
    </div>
  );
};
